import {
  Autocomplete,
  AutocompleteProps,
  Checkbox,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';
import {
  GridEditInputCellProps,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid';
import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { dateFormat } from '@utils/format';
import moment, { Moment } from 'moment';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

type TTextFieldProps = GridRenderEditCellParams<any, any, any> & {
  isPrimaryID?: boolean;
  onValueChange?: (value: string) => void;
} & TextFieldProps;

type TAutocompleteProps = GridRenderEditCellParams<any, any, any> & {
  options?: string[];
  removeDuplicate?: {
    fieldName?: string;
    datatableData: any[];
    relatedField?: {
      fieldName: string;
      options: string[];
      params: any;
    };
    parentField?: {
      fieldName: string;
      options: string[];
      params: any;
    };
  };
} & AutocompleteProps<any, true, true, true>;

type TDatePickerProps = GridRenderEditCellParams<any, any, any> & {
  onValueChange?: (value: Moment | null) => void;
} & DatePickerProps<moment.Moment>;

/**
 * Validates a required field in a grid cell, ensuring that it's not empty
 * unless all other related fields are empty.
 *
 * @param {GridPreProcessEditCellProps | any} params - The parameters for the cell validation, including the field's value and other fields' properties.
 * @returns {Object} An object containing the updated props with an `error` field.
 */
export const validateRequiredField = (
  params: GridPreProcessEditCellProps | any,
) => {
  const allFields = params?.otherFieldsProps;

  const isAllFieldsEmpty = Object.keys(allFields ?? {})?.every(
    (item) =>
      allFields[item].value === '' ||
      allFields[item].value === null ||
      allFields[item].value === undefined,
  );

  return {
    ...params.props,
    error:
      (!params.props?.value || params.props?.value === '-') &&
      !isAllFieldsEmpty,
  };
};

// #region TEXTFIELD
/**
 * Renders a TextField input for editing in a grid cell.
 *
 * @param {GridRenderEditCellParams<any, any, any> & { isPrimaryID?: boolean }} props - The parameters for rendering the cell, including the field's value, ID, and additional properties.
 * @returns {JSX.Element} A TextField component for editing the cell's value.
 */
const GridEditInputCell = forwardRef<
  HTMLInputElement, // Ref type
  TTextFieldProps
>((props, ref) => {
  const {
    id,
    value = '',
    field,
    isPrimaryID,
    hasFocus = false,
    error,
    inputProps,
    InputProps,
    forceRevalidate,
    helperText,
    onValueChange,
    ...other
  } = props;
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>();
  const hasError =
    error ||
    (error &&
      (!value ||
        value === '-' ||
        (isPrimaryID && props.api?.getAllRowIds()?.includes(value))));

  const additionalSx = other?.sx || {};

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue,
    });
    onValueChange?.(newValue);
  };

  useEffect(() => {
    if (forceRevalidate) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: '__temp__force__',
        debounceMs: 0,
      });
      setTimeout(() => {
        apiRef.current.setEditCellValue({
          id,
          field,
          value,
          debounceMs: 0,
        });
      }, 0);
    }
  }, []);

  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [hasFocus]);

  return (
    <Tooltip
      open={!!hasError && helperText}
      title={helperText || 'Required field'}
      arrow
      placement='top-start'
    >
      <TextField
        {...other}
        inputRef={inputRef} // Local ref to manage focus inside the TextField
        ref={ref} // Forwarded ref to parent component
        className={`${other?.className || ''} ${hasError ? 'Mui-error' : ''}`}
        value={value}
        onChange={handleChange}
        size='small'
        fullWidth
        sx={{
          width: '100%!important',
          '& .MuiInputBase-root': {
            fontSize: '12px',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 0 },
          ...additionalSx,
        }}
        inputProps={inputProps}
        InputProps={InputProps}
      />
    </Tooltip>
  );
});

/**
 * Wrapper component for rendering a TextField edit input in the grid.
 *
 * @param {GridEditInputCellProps} params - The props for the cell editor.
 * @returns {JSX.Element} The TextField input component.
 */
export const RenderTextFieldEditInputCell = (
  params: GridEditInputCellProps & {
    isPrimaryID?: boolean;
    forceRevalidate?: boolean;
    helperText?: string;
    onValueChange?: (value: string) => void;
  },
) => <GridEditInputCell {...params} />;
// #endregion

// #region AUTOCOMPLETE
/**
 * Renders an Autocomplete input for editing in a grid cell.
 *
 * @param {GridRenderEditCellParams<any, any, any> & { options?: string[] }} props - The parameters for rendering the cell, including the field's value, ID, and options.
 * @returns {JSX.Element} An Autocomplete component for editing the cell's value.
 */
const GridAutocompleteEditInputCell = forwardRef<
  HTMLInputElement,
  TAutocompleteProps
>((props, ref) => {
  const {
    id,
    value,
    field,
    hasFocus,
    error,
    options = [],
    removeDuplicate,
    onValueChange,
    forceRevalidate,
    helperText,
    ...other
  } = props as TAutocompleteProps;

  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  let hasError = error || (error && (!value || value === '-'));
  const [open, setOpen] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<string | null>(
    null,
  );

  const newOptions = useMemo(() => {
    if (removeDuplicate) {
      const { datatableData, fieldName, relatedField, parentField } =
        removeDuplicate;

      if (relatedField) {
        const {
          fieldName: relatedFieldNames,
          options: relatedOptions,
          params,
        } = relatedField;
        const { field, value } = params;

        /**
         * @typedef {Object} RelatedField
         * @property {string} fieldName - Comma-separated string of related field names.
         * @property {Array} options - Array of related options.
         * @property {Object} params - Parameters for filtering data.
         * @property {string} params.field - The field name to filter by.
         * @property {string} params.value - The value to filter by.
         */

        // Get unique related field names
        const relatedFieldsArray = relatedFieldNames
          .split(',')
          .map((field) => field.trim());

        // Filter relevant data based on params.field and params.value
        const filteredData = datatableData?.filter(
          (item) => item[field] === value,
        );

        // Extract selected options from filtered data
        const selectedOptions = filteredData?.flatMap((row) =>
          relatedFieldsArray
            .map((relatedFieldName) => row?.[relatedFieldName])
            .filter(Boolean),
        );

        // Compare selected options with relatedOptions
        if (
          selectedOptions?.length &&
          JSON.stringify(selectedOptions) === JSON.stringify(relatedOptions)
        ) {
          hasError = true;
          return options?.filter((item) => item !== value);
        }

        return options;
      }

      if (parentField) {
        const {
          fieldName: parentFieldName,
          options: innerOptions,
          params,
        } = parentField;
        const { row } = params;

        /**
         * @typedef {Object} ParentField
         * @property {string} fieldName - The name of the parent field.
         * @property {Array} options - Array of options related to the parent field.
         * @property {Object} params - Parameters for filtering data.
         * @property {Object} params.row - The current row data.
         */

        // Get the parent field value from the current row
        const parentValue = parentFieldName ? row[parentFieldName] : row;

        // Filter and extract relevant data based on the parent field
        const filteredData =
          datatableData
            ?.filter((item) => item[parentFieldName] === parentValue)
            ?.map((item) => item[params?.field]) || [];

        // Return only options that are not in filteredData
        return innerOptions?.filter((item) => !filteredData.includes(item));
      }

      const selectedValues = datatableData.map((row) => row[field]);

      return options.filter((item) =>
        fieldName
          ? !selectedValues.includes(item[fieldName])
          : !selectedValues.includes(item),
      );
    }
    return options;
  }, [options, removeDuplicate, field]);

  const handleChange = async (_event, newValue) => {
    const valueToSet = newValue ?? '';
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: valueToSet,
    });
    onValueChange?.(valueToSet);
    // Clear related fields if specified
    if (removeDuplicate?.relatedField) {
      const relatedFieldsArray =
        removeDuplicate.relatedField.fieldName.split(',');
      relatedFieldsArray.forEach(async (relatedField) => {
        await apiRef.current.setEditCellValue({
          id,
          field: relatedField.trim(),
          value: '',
        });
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
    } else if (event.key === 'Tab') {
      if (open && highlightedOption) {
        handleChange(null as any, highlightedOption);
        event.preventDefault();
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      setTimeout(() => {
        const isRowEdit =
          typeof apiRef.current.getRowMode === 'function' &&
          apiRef.current.getRowMode(id) === 'edit';

        if (isRowEdit) {
          apiRef.current.stopRowEditMode({ id });
        } else {
          apiRef.current.stopCellEditMode({ id, field });
        }
      }, 0);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (forceRevalidate) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: '__temp__force__',
        debounceMs: 0,
      });
      setTimeout(() => {
        apiRef.current.setEditCellValue({
          id,
          field,
          value,
          debounceMs: 0,
        });
      }, 0);
    }
  }, []);

  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [hasFocus]);

  return (
    <Tooltip
      open={!!hasError && helperText}
      title={helperText}
      arrow
      placement='top-start'
    >
      <Autocomplete
        {...other}
        ref={ref}
        className={`${other?.className || ''} ${hasError ? 'Mui-error' : ''}`}
        value={value || ''}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) =>
          value === '' || value === '-' || value === option
        }
        options={newOptions}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderInput={(params) => (
          <TextField {...params} inputRef={inputRef} size='small' />
        )}
        fullWidth
        sx={{
          '& .MuiInputBase-root': {
            fontSize: '12px',
          },
          '& .MuiFormControl-root': {
            width: '100%!important',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 0 },
          '& .MuiButtonBase-root': {
            bgcolor: 'transparent',
          },
          ...other?.sx,
        }}
        onKeyDown={handleKeyDown}
        onHighlightChange={(_event, option) => {
          setHighlightedOption(option);
        }}
      />
    </Tooltip>
  );
});

/**
 * Wrapper component for rendering an Autocomplete edit input in the grid.
 *
 * @param {GridEditInputCellProps} params - The props for the cell editor.
 * @returns {JSX.Element} The Autocomplete input component.
 */
export const RenderAutocompleteEditInputCell = (
  params: GridEditInputCellProps & {
    options?: string[];
    removeDuplicate?: {
      fieldName?: string;
      datatableData: any[];
      relatedField?: {
        fieldName: string;
        options: string[];
        params: any;
      };
      parentField?: {
        fieldName: string;
        options: string[];
        params: any;
      };
    };
    helperText?: string;
    forceRevalidate?: boolean;
  },
) => <GridAutocompleteEditInputCell {...params} />;
// #endregion

// #region DATEPICKER
/**
 * Renders a DatePicker input for editing in a grid cell.
 *
 * @param {GridRenderEditCellParams<any, any, any>} props - The parameters for rendering the cell, including the field's value, ID, and other properties.
 * @returns {JSX.Element} A DatePicker component for editing the cell's value.
 */
const GridDatePickerEditInputCell = forwardRef<
  HTMLInputElement,
  TDatePickerProps
>((props, ref) => {
  const {
    id,
    value,
    field,
    hasFocus,
    error,
    onValueChange,
    forceRevalidate,
    helperText,
    ...other
  } = props;
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasError = error || (error && (!value || value === '-'));
  const additionalSx = other?.sx || {};
  const handleChange = async (newValue: Moment | null) => {
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue ? newValue.toISOString() : null,
    });
    onValueChange?.(newValue);

    const isRowEdit =
      typeof apiRef.current.getRowMode === 'function' &&
      apiRef.current.getRowMode(id) === 'edit';

    setTimeout(() => {
      if (isRowEdit) {
        apiRef.current.stopRowEditMode({ id });
      } else {
        apiRef.current.stopCellEditMode({ id, field });
      }
    }, 0);
  };

  useEffect(() => {
    if (forceRevalidate) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: '__temp__force__',
        debounceMs: 0,
      });
      setTimeout(() => {
        apiRef.current.setEditCellValue({
          id,
          field,
          value,
          debounceMs: 0,
        });
      }, 0);
    }
  }, []);

  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  return (
    <Tooltip
      open={!!hasError && helperText}
      title={helperText || 'Required field'}
      arrow
      placement='top-start'
    >
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          {...other}
          inputRef={inputRef}
          ref={ref}
          className={`${other?.className || ''} ${hasError ? 'Mui-error' : ''}`}
          format={dateFormat}
          value={value ? moment(value) : null}
          onChange={handleChange}
          slotProps={{
            field: { clearable: false },
            textField: {
              size: 'small',
              placeholder: dateFormat,
            },
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': {
                borderColor: 'transparent',
              },
            },
            '& .MuiOutlinedInput-root:hover': {
              '& > fieldset': {
                borderColor: 'transparent',
              },
            },
            '& .MuiInputAdornment-root button': {
              border: 0,
              color: '#161C248F',
              bgcolor: 'transparent',
            },
            input: {
              fontSize: '12px',
            },
            ...additionalSx,
          }}
        />
      </LocalizationProvider>
    </Tooltip>
  );
});

/**
 * Wrapper component for rendering a DatePicker edit in the grid.
 *
 * @param {GridEditInputCellProps} params - The props for the cell editor.
 * @returns {JSX.Element} The Select input component.
 */
export const RenderDatePickerEditInputCell = (
  params: GridEditInputCellProps & {
    forceRevalidate?: boolean;
    helperText?: string;
    minDate?: moment.Moment;
  },
) => <GridDatePickerEditInputCell {...params} />;
// #endregion

// #region CHECKBOX
type TCheckboxEditInputCellProps = GridRenderEditCellParams & {
  forceRevalidate?: boolean;
  helperText?: string;
  onValueChange?: (checked: boolean) => void | Promise<void>;
};

const GridCheckboxEditInputCell = forwardRef<
  HTMLInputElement,
  TCheckboxEditInputCellProps
>((props, ref) => {
  const {
    id,
    value,
    field,
    hasFocus,
    error,
    forceRevalidate,
    helperText,
    onValueChange,
    ...other
  } = props;
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasError = error || (error && (!value || value === '-'));

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: event.target.checked,
    });
    await onValueChange?.(event.target.checked);
  };

  useEffect(() => {
    if (forceRevalidate) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: '__temp__force__',
        debounceMs: 0,
      });
      setTimeout(() => {
        apiRef.current.setEditCellValue({
          id,
          field,
          value,
          debounceMs: 0,
        });
      }, 0);
    }
  }, []);

  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  const combinedRef = (node: HTMLInputElement | null) => {
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }

    inputRef.current = node;
  };
  return (
    <Tooltip
      open={!!hasError && helperText}
      title={helperText || 'Required field'}
      arrow
      placement='top-start'
    >
      <Checkbox
        {...other}
        inputRef={combinedRef}
        checked={!!value}
        onChange={handleChange}
        size='medium'
      />
    </Tooltip>
  );
});

/**
 * Wrapper component for rendering a Checkbox input for editing in a grid cell.
 *
 * @param {GridRenderEditCellParams} params - The props for the cell editor.
 * @returns {JSX.Element} The Checkbox input component.
 */
export const RenderCheckboxEditInputCell = (
  params: TCheckboxEditInputCellProps,
) => <GridCheckboxEditInputCell {...params} />;
// #endregion CHECKBOX
